import type { GuestBookingFailure } from "../guestCheckout/port";

/*
 * rc.11 1c: WHEN A REPLAY'S REFUSAL MEANS "NO BOOKING UNDER THIS KEY" (Backend,
 * in writing: integration/rc4 createBooking.ts; the Web Director's rule). One
 * rule for the guest checkout (guestCheckout/machine.ts) and the signed-in
 * booking (useBooking.ts): they share createBooking.
 *
 * Backend reads the receipt keyed by (uid, key) BEFORE the hold, the profile,
 * the proof and the Terms, with a strongly consistent get: a committed
 * original replays there whatever else has changed, and a different request
 * under the key is a conflict there. So a later TYPED business refusal (the
 * allow-list below) says there is no receipt, but only if BOTH:
 *   (i)  the replay went under the SAME uid (another identity misses the
 *        receipt and proves nothing); and
 *   (ii) it went at or after max(the hold's expiry, the LATEST lost send +
 *        60 s) + 30 s: a lost answer can be a 504 while that call is still
 *        running (up to 60 s), and until the hold ends it can still commit.
 *        A Check is itself a full send, so a lost Check counts.
 * Refusals from before the receipt read (rate limit, the phone gate, input
 * validation), an untyped or unknown one, a conflict (a receipt EXISTS) and an
 * existing booking prove nothing: the uncertainty stays.
 *
 * DEPENDS ON BACKEND (record: docs/testing/release-v1.0.0-rc.11.md): the
 * stage order (the receipt read before the hold and every business check),
 * the receipt re-guarded inside the transaction, the hold re-checked inside
 * it, and apiApp's 60 s function timeout. A change to any of them changes
 * this rule.
 *
 * MEASURED SAFELY ON THE CLIENT. The browser cannot read the server's time.
 * So the 60 s and the wait are measured on the monotonic clock from the send,
 * and the hold's expiry (the server's instant) is read once against the wall
 * clock at the send, plus REPLAY_CLOCK_SKEW_PAD_MS. A clock BEHIND the
 * server only lengthens the wait; one ahead by up to the pad is covered. A
 * monotonic clock that pauses in sleep also only lengthens it. The cost (Web
 * Director, accepted): "may have gone through" for up to about five minutes
 * after Backend would already answer "none".
 */
export const REPLAY_RUN_MS = 60_000;
export const REPLAY_RECEIPT_PAD_MS = 30_000;
export const REPLAY_CLOCK_SKEW_PAD_MS = 5 * 60_000;

/** The instants a replay's answer is judged against. Never sent. */
export interface SendFacts {
  /** `performance.now()` at the send: monotonic, never set back. */
  readonly mono: number;
  /** The client's wall clock at the send, read once against `holdExpiresAt`. */
  readonly at: number;
  /** The hold's expiry as the server sent it (ms since epoch); NaN when unknown. */
  readonly holdExpiresAt: number;
}

/** The monotonic instant from which a replay can be definite after this send; null: never. */
export function definiteFrom(sent: SendFacts | null | undefined): number | null {
  if (!sent || !Number.isFinite(sent.mono) || !Number.isFinite(sent.at) || !Number.isFinite(sent.holdExpiresAt)) return null;
  const holdLeft = Math.max(0, sent.holdExpiresAt - sent.at) + REPLAY_CLOCK_SKEW_PAD_MS;
  return sent.mono + Math.max(holdLeft, REPLAY_RUN_MS) + REPLAY_RECEIPT_PAD_MS;
}

/** Whether a replay sent at `mono` is past the threshold of EVERY send listed. */
export function pastEvery(mono: number, sends: readonly (SendFacts | null | undefined)[]): boolean {
  if (!Number.isFinite(mono)) return false;
  for (const sent of sends) {
    const from = definiteFrom(sent);
    if (from === null || mono < from) return false;
  }
  return true;
}

/**
 * THE ALLOW-LIST (Backend, in writing): the refusals, decided before the
 * commit, that can mean "no booking under this key". An ALLOW-list, so a code
 * this client does not know is never definite. The generic `refused` (an
 * unknown or untyped code) is deliberately absent.
 */
export function refusalCanBeDefinite(failure: GuestBookingFailure): boolean {
  switch (failure.kind) {
    case "hold-gone": // HOLD_GONE, and NOT_FOUND from inside the transaction
    case "slot-unavailable": // SLOT_UNAVAILABLE from inside the transaction
    case "guest-unverified": // the guest-proof refusals
    case "settings-changed": // the profile, service and settings refusals
    case "payment-mode-unavailable":
    // CLIENT_ACCOUNT_REQUIRED: assertGuestBookingAllowed (createBooking.ts:272),
    // after the receipt read, the hold and the profile load, before any write.
    case "account-required":
    // GUEST_BOOKING_UNAVAILABLE (the keyring), just after that check, before any write.
    case "guest-unavailable":
    // PAYMENT_TERMS_CHANGED: Backend in writing, plan/payments-v1.1.0 rev 3
    // §11.3 item 8: recorded on the key's receipt, nothing booked, definite.
    case "payment-terms-changed":
    // PAYMENT_MODE_NOT_SUPPORTED_BY_CLIENT: Backend in writing (2026-10-17),
    // "nothing booked or held": createBooking's capability gate
    // (createBooking.ts:383, on 6b) runs after the receipt pre-read (:199)
    // and before the first write (:515); createSlotHold's (:138) before its
    // only write (:243).
    case "payment-client-unsupported":
      return true;
    case "terms": // TERMS_STALE / TERMS_ACCEPTANCE_REQUIRED, never TERMS_UNAVAILABLE
      return failure.refusal === "stale" || failure.refusal === "required";
    default:
      return false;
  }
}
